import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth, useClerk, useUser } from "@clerk/react";
import { useNavigate } from "react-router-dom";
import { createRazorpayOrder, verifyRazorpayPayment } from "./paymentApi";
import { useEntitlement } from "./EntitlementContext";

const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;
const PENDING_PAYMENT_KEY = "razorpay_pending_payment";

export const useCoursePurchase = () => {
  const [paymentMessage, setPaymentMessage] = useState("");
  const [paymentError, setPaymentError] = useState("");
  const [isPaying, setIsPaying] = useState(false);
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { openSignIn } = useClerk();
  const { user } = useUser();
  const { hasActiveEntitlement, reloadEntitlement, updateEntitlement } =
    useEntitlement();
  const navigate = useNavigate();
  const checkoutResolvedRef = useRef(false);
  const checkoutRef = useRef(null);
  const pendingVerificationRef = useRef(false);

  const clearPendingPayment = useCallback(() => {
    window.sessionStorage.removeItem(PENDING_PAYMENT_KEY);
  }, []);

  const savePendingPayment = useCallback((payment) => {
    window.sessionStorage.setItem(
      PENDING_PAYMENT_KEY,
      JSON.stringify({
        razorpay_payment_id: payment?.razorpay_payment_id,
        razorpay_order_id: payment?.razorpay_order_id,
        razorpay_signature: payment?.razorpay_signature,
      })
    );
  }, []);

  const finishSuccessfulPayment = useCallback(() => {
    clearPendingPayment();
    setPaymentMessage("Payment successful.");
    navigate("/enter-studio");
  }, [clearPendingPayment, navigate]);

  const resetCheckoutState = (message) => {
    checkoutResolvedRef.current = true;
    setIsPaying(false);

    if (message) {
      setPaymentError(message);
    }

    window.setTimeout(() => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }, 0);
  };

  const commitVerifiedEntitlement = useCallback(
    async (verifiedEntitlement) => {
      if (verifiedEntitlement?.active) {
        updateEntitlement(verifiedEntitlement);
        return verifiedEntitlement;
      }

      const entitlement = await reloadEntitlement();

      if (entitlement?.active) {
        updateEntitlement(entitlement);
      }

      return entitlement;
    },
    [reloadEntitlement, updateEntitlement]
  );

  useEffect(() => {
    return () => {
      checkoutRef.current?.close?.();
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const verifyPendingPayment = async () => {
      if (!isLoaded || !isSignedIn || pendingVerificationRef.current) return;

      const pendingPayment = window.sessionStorage.getItem(PENDING_PAYMENT_KEY);
      if (!pendingPayment) return;

      pendingVerificationRef.current = true;
      setIsPaying(true);
      setPaymentError("");
      setPaymentMessage("Confirming payment...");

      try {
        const payment = JSON.parse(pendingPayment);
        const verification = await verifyRazorpayPayment(getToken, payment);
        const entitlement = await commitVerifiedEntitlement(
          verification?.entitlement
        );

        if (!entitlement?.active) {
          throw new Error("Payment verified, but access is not active yet.");
        }

        finishSuccessfulPayment();
      } catch (err) {
        const entitlement = await reloadEntitlement();

        if (entitlement?.active) {
          finishSuccessfulPayment();
        } else {
          setPaymentError(
            err.message || "Payment verification failed. Please try again."
          );
        }
      } finally {
        pendingVerificationRef.current = false;
        setIsPaying(false);
      }
    };

    verifyPendingPayment();
  }, [
    commitVerifiedEntitlement,
    finishSuccessfulPayment,
    getToken,
    isLoaded,
    isSignedIn,
    reloadEntitlement,
  ]);

  const startPurchase = async () => {
    if (isPaying) return;

    setPaymentError("");
    setPaymentMessage("");
    checkoutResolvedRef.current = false;

    if (!isLoaded) {
      setPaymentError("Account is still loading. Please try again.");
      return;
    }

    if (!isSignedIn) {
      openSignIn();
      return;
    }

    if (!RAZORPAY_KEY_ID) {
      setPaymentError("Razorpay is not configured for this client.");
      return;
    }

    if (!window.Razorpay) {
      setPaymentError("Payment checkout is still loading. Please try again.");
      return;
    }

    setIsPaying(true);

    try {
      const currentEntitlement = hasActiveEntitlement
        ? { active: true }
        : await reloadEntitlement();

      if (currentEntitlement?.active) {
        clearPendingPayment();
        setPaymentMessage("Your course access is already active.");
        setIsPaying(false);
        navigate("/enter-studio");
        return;
      }

      const order = await createRazorpayOrder(getToken);

      if (order.alreadyPurchased) {
        clearPendingPayment();
        await commitVerifiedEntitlement(order.entitlement);
        setPaymentMessage("Your course access is already active.");
        setIsPaying(false);
        navigate("/enter-studio");
        return;
      }

      const checkout = new window.Razorpay({
        key: RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: "Kirubeezy Studios",
        description: "Vocal Mixing Masterclass",
        order_id: order.order_id,
        prefill: {
          name: user?.fullName || "",
          email: user?.primaryEmailAddress?.emailAddress || "",
        },
        theme: {
          color: "#C5A059",
        },
        modal: {
          ondismiss: () => {
            if (checkoutResolvedRef.current) return;

            resetCheckoutState("Payment was cancelled.");
          },
        },
        retry: {
          enabled: false,
        },
        handler: async (response) => {
          checkoutResolvedRef.current = true;
          savePendingPayment(response);
          setPaymentMessage("Confirming payment...");

          try {
            const verification = await verifyRazorpayPayment(getToken, response);
            const entitlement = await commitVerifiedEntitlement(
              verification?.entitlement
            );

            if (!entitlement?.active) {
              throw new Error("Payment confirmed, but access is still updating.");
            }

            finishSuccessfulPayment();
          } catch (err) {
            const entitlement = await reloadEntitlement();

            if (entitlement?.active) {
              finishSuccessfulPayment();
              return;
            }

            setPaymentError(
              err.message ||
                "Payment verification is pending. Please refresh your access in a moment."
            );
          } finally {
            setIsPaying(false);
          }
        },
      });
      checkoutRef.current = checkout;

      checkout.on("payment.failed", (response) => {
        if (checkoutResolvedRef.current) return;

        checkoutResolvedRef.current = true;
        checkout.close?.();
        resetCheckoutState(
          response?.error?.description || "Payment failed. Please try again."
        );
      });

      checkout.open();
    } catch (err) {
      resetCheckoutState(err.message || "Unable to start payment.");
    }
  };

  return {
    isPaying,
    paymentError,
    paymentMessage,
    startPurchase,
  };
};
